// useAiCollectionSettingsModal.js

import { useCallback } from 'react';

import { toast } from 'react-toastify';

import useOpenDatabaseSettingsModal from 'collection/hooks/useOpenDatabaseSettingsModal';
import { isLazySystemDatabaseType } from 'utilities/lazySystemDatabaseTypes';

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Opens DatabaseModal from Ai chat actions after a database is created.
 * Chat only allows reconfigure of custom (non-system) collections.
 */
export default function useAiCollectionSettingsModal() {
  const {
    canManageTypeSchema,
    canOpenSettingsForType,
    databaseModalProps,
    isOpening,
    openDatabaseSettings: openShared,
  } = useOpenDatabaseSettingsModal({
    allowSystemTypes: false,
  });

  const canConfigureTypeKey = useCallback((actionTypeKey = '') => {
    const cleanTypeKey = normalizeString(actionTypeKey);
    if (isLazySystemDatabaseType(cleanTypeKey)) return false;
    return canOpenSettingsForType(cleanTypeKey);
  }, [canOpenSettingsForType]);

  const openDatabaseSettings = useCallback(async (options = {}) => {
    const cleanTypeKey = normalizeString(options?.typeKey);
    if (cleanTypeKey && !canConfigureTypeKey(cleanTypeKey)) {
      toast.error('Built-in types like Photos can’t be reconfigured from chat.');
      return;
    }
    if (!canManageTypeSchema) {
      toast.error('You do not have permission to configure collections in this base.');
      return;
    }
    return openShared(options);
  }, [canConfigureTypeKey, canManageTypeSchema, openShared]);

  return {
    canConfigureDatabase: canManageTypeSchema,
    canConfigureTypeKey,
    canManageTypeSchema,
    databaseModalProps,
    isOpening,
    openDatabaseSettings,
  };
}
